/**
 * TRUST DETAIL (Phase 3). One trust entity in full: its registration, the trustee, the beneficiary
 * structure, the asset it holds, and the record of distributions it has made. The trust certificate is
 * the document a holder can point to as proof of the legal structure behind their token.
 */

import { fmtDate } from '@/lib/format';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal } from '@/components/motion';

export default function TrustDetail() {
  const params = useParams();
  const {
    data: trust,
    loading,
    error,
    reload,
  } = useResource(() => api.trust(params.id ?? ''), [params.id]);

  if (loading) return <LoadingCard lines={5} />;
  if (error) return <ErrorState body="We could not load this trust." onRetry={reload} />;
  if (!trust) return null;

  return (
    <div className="space-y-5">
      <Link
        to="/trust"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Trust entities
      </Link>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        {/* MAIN */}
        <div className="space-y-5 lg:col-span-2">
          {/* CERTIFICATE HEADER */}
          <Reveal>
            <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-6 text-white shadow-lifted">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
              />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <span className="rounded-pill bg-white/20 px-2.5 py-0.5 text-micro font-medium capitalize backdrop-blur">
                    {trust.kind} &middot; {trust.status}
                  </span>
                </div>
                <h1 className="mt-3 text-2xl font-semibold tracking-tight">{trust.legal_name}</h1>
                <p className="mt-1 text-sm text-white/80">
                  Registered in {trust.jurisdiction} &middot; {trust.registration_number}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-white/85">
                  This trust holds {trust.asset}. Tokens issued against it represent a beneficial
                  interest in the trust, not direct ownership of the asset.
                </p>
              </div>
            </section>
          </Reveal>

          {/* STRUCTURE */}
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Structure</h2>
            <dl className="mt-3 divide-y divide-border text-sm">
              <Row label="Trustee" value={trust.trustee} />
              <Row label="Jurisdiction" value={trust.jurisdiction} />
              <Row label="Registration number" value={trust.registration_number} mono />
              <Row label="Beneficiaries" value={String(trust.beneficiaries)} />
              <Row label="Token holders" value={String(trust.holders)} />
              <Row label="Current valuation" value={`KES ${trust.valuation}`} />
              <Row label="Established" value={fmtDate(trust.created_at)} />
            </dl>
          </section>

          {/* DISTRIBUTIONS */}
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Distribution records</h2>
            {trust.distributions.length === 0 ? (
              <p className="mt-2 text-sm text-ink-muted">No distributions yet.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {trust.distributions.map((d, i) => (
                  <li key={i} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{d.kind}</p>
                      <p className="text-xs text-ink-subtle">{fmtDate(d.at)}</p>
                    </div>
                    <span className="tabular shrink-0 text-sm font-semibold text-accent">
                      KES {d.amount}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* RAIL: documents + actions */}
        <div className="space-y-4">
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Documents</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {['Trust certificate', 'Trust deed', 'Trustee appointment', 'Asset title'].map(
                (doc) => (
                  <li key={doc}>
                    <button className="flex w-full items-center justify-between rounded-field border border-border px-3 py-2.5 text-left transition hover:bg-surface-sunken">
                      <span className="text-ink">{doc}</span>
                      <svg
                        className="h-4 w-4 text-ink-subtle"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14"
                        />
                      </svg>
                    </button>
                  </li>
                ),
              )}
            </ul>
          </section>

          <button className="w-full rounded-field bg-primary py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light">
            Download trust certificate
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`text-right font-medium text-ink ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
